import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { format } from "date-fns";
import { usePartialUpdateContact, useRelationshipTypes } from "@/hooks/api/useContacts";
import { RelationshipTypeEnum, Contact } from "@/types/api";
import { useToast } from "@/hooks/use-toast";
import AuthErrorDialog from "./AuthErrorDialog";
import { DatePicker } from "./DatePicker";

interface Step3ContactDetailsProps {
  contact: Contact;
  onContinue: () => void;
  onSkip: () => void;
  isLastContact: boolean;
}

const Step3ContactDetails = ({ 
  contact, 
  onContinue, 
  onSkip,
  isLastContact 
}: Step3ContactDetailsProps) => {
  const [relationshipType, setRelationshipType] = useState<RelationshipTypeEnum | "">("");
  const [birthday, setBirthday] = useState<Date | undefined>(
    contact.birthday ? new Date(contact.birthday) : undefined
  );
  const [customFields, setCustomFields] = useState<Record<string, any>>({});
  const [showAuthError, setShowAuthError] = useState(false);
  const { toast } = useToast();
  
  const updateContact = usePartialUpdateContact(contact.id);
  const { data: relationshipTypes, isLoading: isLoadingTypes } = useRelationshipTypes();

  // Initialize with existing contact data
  useEffect(() => {
    if (contact.relationship_type) {
      setRelationshipType(contact.relationship_type as RelationshipTypeEnum);
    }
    if (contact.custom_data) {
      setCustomFields(contact.custom_data);
    }
  }, [contact]);

  const handleSave = async () => {
    try {
      // Validate required fields first
      const dynamicFields = getDynamicFields();
      const requiredFields = dynamicFields.filter((field: any) => field.required);
      
      // Check if all required fields are filled
      for (const field of requiredFields) {
        const value = customFields[field.name];
        if (!value && value !== false && value !== 0) {
          toast({
            title: "Missing required field",
            description: `Please fill in the ${field.name.replace(/_/g, ' ')} field`,
            variant: "destructive",
          });
          return;
        }
      }
      
      // Separate dynamic relationship fields from other custom data
      const relationshipData: Record<string, any> = {};
      const otherCustomData: Record<string, any> = {};
      
      // Get all dynamic field names for this relationship type
      const dynamicFieldNames = new Set(dynamicFields.map((field: any) => field.name));
      
      Object.keys(customFields).forEach(key => {
        const value = customFields[key];
        // Only include fields that have actual values (not empty strings, null, or undefined)
        if (value !== '' && value !== null && value !== undefined) {
          if (dynamicFieldNames.has(key)) {
            relationshipData[key] = value;
          } else {
            // Only include in custom_data if it's not a relationship field
            otherCustomData[key] = value;
          }
        }
      });

      // Only include fields that we want to update
      const updateData: any = {};
      
      // Only include relationship_type if it's set
      if (relationshipType) {
        updateData.relationship_type = relationshipType;
      }
      
      // Only include birthday if it's set or explicitly cleared
      if (birthday) {
        updateData.birthday = format(birthday, "yyyy-MM-dd");
      }
      
      // Only include relationship_data if there are fields to update
      if (Object.keys(relationshipData).length > 0) {
        updateData.relationship_data = relationshipData;
      }
      
      // Only include custom_data if there are actual custom fields
      if (Object.keys(otherCustomData).length > 0) {
        updateData.custom_data = otherCustomData;
      }

      await updateContact.mutateAsync(updateData);
      
      toast({
        title: "Contact details saved",
        description: `Details for ${contact.name} have been updated.`,
      });
      
      onContinue();
    } catch (error: any) {
      console.error("Failed to update contact:", error);
      
      // Check if it's an authentication error
      if (error?.response?.status === 401) {
        setShowAuthError(true);
        return;
      }
      
      // Show error dialog
      const errorMessage = error?.response?.data?.error || error?.message || "Failed to save contact details";
      
      toast({
        title: "Error saving contact",
        description: errorMessage,
        variant: "destructive",
      });
      
      // Don't continue to next contact on error
      // The user can retry or skip
    }
  };

  const handleSkip = () => {
    onSkip();
  };

  // Get dynamic fields based on relationship type
  const getDynamicFields = () => {
    if (!relationshipTypes || !relationshipType) return [];
    
    // The relationship types API returns an array of relationship type objects
    // Find the one matching our selected relationship type
    const typeData = relationshipTypes.find((rt: any) => rt.type === relationshipType);
    return typeData?.fields || [];
  };

  // Update custom fields when relationship type changes
  useEffect(() => {
    if (relationshipType && relationshipTypes && Array.isArray(relationshipTypes)) {
      const fields = getDynamicFields();
      // Only initialize boolean fields with false, leave others undefined
      setCustomFields(prevFields => {
        const newFields = { ...prevFields };
        fields.forEach((field: any) => {
          // Only set default value for boolean fields
          if (field.type === 'BooleanField' && !(field.name in newFields)) {
            newFields[field.name] = false;
          }
          // Don't initialize other field types - let them remain undefined
        });
        return newFields;
      });
    }
  }, [relationshipType]); // Only depend on relationshipType change

  return (
    <div className="flex min-h-screen items-center justify-center px-4 bg-white">
      <div className="w-full max-w-[621px] shadow-md p-8 md:p-[56px] rounded-md mb-16">
        <div className="mb-8">
          <p className="text-[#6C4F77]/50 text-sm font-normal mb-2">
            Step 3 of 3 • Add details for {contact.name}
          </p>
          <h1 className="text-[20px] md:text-[32px] font-bold text-[#6C4F77] mb-2 font-modernist">
            Personalize this contact
          </h1>
          <p className="text-[#666666] text-base text-[14px] md:text-[16px] font-normal">
            Add details to help you remember what matters about them
          </p>
        </div>

        <div className="space-y-6">
          {/* Relationship Type Dropdown */}
          <div className="space-y-2">
            <Label htmlFor="relationship" className="text-sm font-medium text-gray-700 px-1">
              How do you refer to them?
            </Label>
            <Select
              value={relationshipType}
              onValueChange={(value) => setRelationshipType(value as RelationshipTypeEnum)}
              disabled={isLoadingTypes}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={isLoadingTypes ? "Loading..." : "Click the dropdown to select"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="mother">Mother</SelectItem>
                <SelectItem value="father">Father</SelectItem>
                <SelectItem value="sister">Sister</SelectItem>
                <SelectItem value="brother">Brother</SelectItem>
                <SelectItem value="spouse">Spouse</SelectItem>
                <SelectItem value="child">Child</SelectItem>
                <SelectItem value="friend">Friend</SelectItem>
                <SelectItem value="colleague">Colleague</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Birthday Field */}
          <div className="space-y-2">
            <Label htmlFor="birthday" className="text-sm font-medium text-gray-700 px-1">
              Birthday
            </Label>
            <DatePicker
              id="birthday"
              value={birthday}
              onChange={setBirthday}
              placeholder="Select date"
            />
          </div>

          {/* Dynamic fields based on relationship type */}
          {relationshipType && getDynamicFields().length > 0 && (
            <>
              {getDynamicFields().map((field: any) => {
                // Convert field type from Django to HTML input type
                const getInputType = () => {
                  switch(field.type) {
                    case 'CharField':
                    case 'TextField':
                      return 'text';
                    case 'DateField':
                      return 'date';
                    case 'BooleanField':
                      return 'checkbox';
                    default:
                      return 'text';
                  }
                };

                const inputType = getInputType();
                
                // Format field name to a readable label
                const formatLabel = (name: string) => {
                  return name
                    .split('_')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ');
                };

                if (inputType === 'checkbox') {
                  return (
                    <div key={field.name} className="flex items-center space-x-2">
                      <input
                        id={field.name}
                        type="checkbox"
                        checked={customFields[field.name] || false}
                        onChange={(e) => setCustomFields({
                          ...customFields,
                          [field.name]: e.target.checked
                        })}
                        className="h-4 w-4 text-[#6C4F77] focus:ring-[#6C4F77] border-gray-300 rounded"
                      />
                      <Label 
                        htmlFor={field.name} 
                        className="text-sm font-medium text-gray-700 cursor-pointer"
                      >
                        {formatLabel(field.name)}
                      </Label>
                    </div>
                  );
                }

                // Handle date fields with shadcn date picker
                if (inputType === 'date') {
                  return (
                    <div key={field.name} className="space-y-2">
                      <Label htmlFor={field.name} className="text-sm font-medium text-gray-700 px-1">
                        {formatLabel(field.name)}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                      </Label>
                      <DatePicker
                        id={field.name}
                        value={customFields[field.name] ? new Date(customFields[field.name]) : undefined}
                        onChange={(date) => {
                          setCustomFields({
                            ...customFields,
                            [field.name]: date ? format(date, "yyyy-MM-dd") : ""
                          });
                        }}
                        placeholder={field.help_text || `Select ${formatLabel(field.name).toLowerCase()}`}
                      />
                    </div>
                  );
                }

                return (
                  <div key={field.name} className="space-y-2">
                    <Label htmlFor={field.name} className="text-sm font-medium text-gray-700 px-1">
                      {formatLabel(field.name)}
                      {field.required && <span className="text-red-500 ml-1">*</span>}
                    </Label>
                    <Input
                      id={field.name}
                      type={inputType}
                      placeholder={field.help_text || `Enter ${formatLabel(field.name).toLowerCase()}`}
                      value={customFields[field.name] || ""}
                      onChange={(e) => setCustomFields({
                        ...customFields,
                        [field.name]: e.target.value
                      })}
                      required={field.required}
                      className="w-full"
                    />
                  </div>
                );
              })}
            </>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 !mt-[48px]">
            <Button
              variant="outline"
              onClick={handleSkip}
              className="flex-1 border-gray-200 text-gray-600 font-normal text-[14px] font-modernist py-[20px]"
            >
              Skip
            </Button>
            
            <Button
              onClick={handleSave}
              disabled={updateContact.isPending}
              className="flex-1 bg-[#6C4F77] hover:bg-[#5A3A68] disabled:bg-gray-300 text-white font-bold text-[14px] font-modernist py-[20px]"
            >
              {updateContact.isPending 
                ? "Saving..." 
                : isLastContact 
                  ? "Save & Complete" 
                  : "Save & Next"}
            </Button>
          </div>
        </div>
      </div>
      
      <AuthErrorDialog 
        isOpen={showAuthError} 
        onClose={() => setShowAuthError(false)} 
      />
    </div>
  );
};

export default Step3ContactDetails;